import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { uniq } from 'lodash';
import { BehaviorSubject, Observable, filter, firstValueFrom, switchMap, timeout } from 'rxjs';
import {
    AssetApiService,
    IAsset,
    IAssetMapGroup,
    IAssetMapResult,
    IAssetScore,
    IAssetType,
    ILocalizedStringAssetScoreType,
    IMapViewPort,
    INamedAssetType,
    LocaleStringMap,
} from 'shared';
import { v4 as uuid } from 'uuid';

import { LazyTranslateLoader } from '../../utils/lazy-translate-loader';
import { AppStateService } from './state';

interface BaseAssetQuery {
    typeId?: string;
}

interface AssetMapQuery extends BaseAssetQuery {
    viewPort: IMapViewPort;
    floor: number;
    gridSize: [number, number];
}

@Injectable({ providedIn: 'root' })
export class AssetService {
    private assetApiService = inject(AssetApiService);
    private translate = inject(TranslateService);
    private readonly appState = inject(AppStateService);

    private typeCache: { [id: string]: INamedAssetType } = {};
    private readonly _assetTypes: BehaviorSubject<Array<INamedAssetType>> = new BehaviorSubject<Array<INamedAssetType>>(
        []
    );

    public readonly assetTypes: Observable<Array<INamedAssetType>> = this._assetTypes.asObservable();

    private readonly _assetMapGroups: BehaviorSubject<Array<IAssetMapGroup>> = new BehaviorSubject<
        Array<IAssetMapGroup>
    >([]);

    private readonly _mapAssets: BehaviorSubject<Array<IAsset>> = new BehaviorSubject<Array<IAsset>>([]);
    public readonly mapAssets: Observable<Array<IAsset>> = this._mapAssets.asObservable();

    public constructor() {
        // Clear data on logout
        this.appState.user.distinctStream.pipe(filter((user) => !user)).subscribe((_) => {
            this._assetTypes.next([]);
        });
        // Refresh on socket connect
        this.appState.socketStatus.distinctStream
            .pipe(
                filter((s) => s.status === 'CONNECTED'),
                // Wait for the user to be known
                switchMap(() =>
                    firstValueFrom(
                        this.appState.user.distinctStream.pipe(
                            filter((user) => !!user),
                            timeout(3000)
                        )
                    )
                ),
                // Refresh everything
                switchMap(() => Promise.all([this.refreshAssetTypes()]))
            )
            .subscribe();
    }

    //
    // Data fetching
    //

    public async refreshAssetTypes(): Promise<Array<IAssetType>> {
        // Get asset types
        const assetTypes = await firstValueFrom(this.assetApiService.getAssetTypes());
        // Wait for base translations to have loaded
        await firstValueFrom((this.translate.currentLoader as LazyTranslateLoader).translationsLoaded$);
        // Extract translations and append them
        assetTypes.forEach((type) => this.applyTranslationsForAssetType(type));
        // Map to named type
        const namedAssetTypes = assetTypes.map(AssetService.mapAssetTypeToNamedType);
        // Publish asset types and return them
        this._assetTypes.next(namedAssetTypes);
        return assetTypes;
    }

    public getAsset(id: string): Observable<IAsset> {
        return this.assetApiService.getAsset(id);
    }

    public async executeMapQuery(query: AssetMapQuery): Promise<IAssetMapResult> {
        // Run query
        const result = await firstValueFrom(
            this.assetApiService.getAssetGroups(query.floor, query.viewPort, query.gridSize)
        );
        // Cache relevant asset types
        await this.cacheAssetTypes((result.assets || []).map((a) => a.typeId));
        // Inject random uuids to groups for easier handling
        if (result.groups) result.groups.forEach((g) => (g.id = uuid()));
        // Update map resources
        this._assetMapGroups.next(result.groups || []);
        this._mapAssets.next(result.assets || []);
        // Return query result
        return result;
    }

    public async cacheAssetTypes(typeIds: Array<string>): Promise<void> {
        const uncachedTypeIds = uniq(typeIds).filter((typeId) => typeId && !this.typeCache[typeId]);
        if (!uncachedTypeIds.length) return;
        const assetTypes = await firstValueFrom(this.assetApiService.getAssetTypes());
        assetTypes.forEach((type) => this.cacheAssetType(type));
    }

    public async getAssetType(typeId: string): Promise<INamedAssetType | null> {
        // Return from cache if available
        if (this.typeCache[typeId]) return this.typeCache[typeId];
        // Fetch from API
        const type = await firstValueFrom(this.assetApiService.getAssetType(typeId));
        if (!type) {
            console.warn('Could not cache asset type for typeid', typeId);
            return null;
        }
        return this.cacheAssetType(type);
    }

    // Gets (a very inaccurate) approximation of the viewport from a GeoJson polygon.
    // This is a temporary solution until the asset service is updated to take GeoJson polygons as input for the viewport.
    public getViewPortFromPolygon(polygon: GeoJSON.Polygon): IMapViewPort {
        const outer = polygon.coordinates[0];
        const latitudes = outer.map((p) => p[1]);
        const longitudes = outer.map((p) => p[0]);
        const minLat = Math.min(...latitudes);
        const maxLat = Math.max(...latitudes);
        const minLong = Math.min(...longitudes);
        const maxLong = Math.max(...longitudes);
        return {
            a: [minLat, minLong],
            b: [maxLat, maxLong],
        };
    }

    public async getScoreForAssetScore(score?: IAssetScore): Promise<string | null> {
        if (!score) return null;
        switch (score.scoreType) {
            case 'Number': {
                return score.numberScore.toString(10);
            }
            case 'LocalizedString': {
                if (!score.assetTypeId) return null;
                const assetType = await this.getAssetType(score.assetTypeId);
                if (!assetType) return null;
                const stringScore = score.uuidScore;
                if (!stringScore) return null;
                const optionNames = (assetType.scoreType as ILocalizedStringAssetScoreType).allowedValues.find(
                    (value) => value.id === stringScore
                )?.name as LocaleStringMap;
                if (!optionNames)
                    console.warn(`Could not find score option '${stringScore}' on assetType '${score.assetTypeId}'`);
                return optionNames?.[this.translate.currentLang] ?? optionNames?.en ?? '-';
            }
            default:
                return null;
        }
    }

    //
    // Utilities
    //

    private applyTranslationsForAssetType(type: IAssetType): void {
        if (type.name && typeof type.name !== 'object') return;
        Object.entries(type.name).forEach(([lang, name]) => {
            this.translate.setTranslation(lang, { assetType: { [type.id]: name } }, true);
        });
    }

    private cacheAssetType(type: IAssetType): INamedAssetType {
        this.applyTranslationsForAssetType(type);
        return (this.typeCache[type.id] = {
            ...type,
            name: typeof type.name === 'object' ? `assetType.${type.id}` : type.name,
        });
    }

    //
    // Actions
    //

    private static mapAssetTypeToNamedType(type: IAssetType): INamedAssetType {
        return {
            id: type.id,
            name: typeof type.name === 'object' ? `assetType.${type.id}` : type.name,
        };
    }
}
